// src/components/DashboardMain.jsx
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Line, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";

import api from "../services/api";
import TransactionModal from "./TransactionModal";
import TransactionHistoryModal from "./TransactionHistoryModal";
import TransactionStatusBadge from "./TransactionStatusBadge";
import InvestmentModal from "./InvestmentModal";
import { AuthContext } from "../context/AuthContext";
import { instrumentMetaByValue, instrumentOptions, normalizeInstrumentType } from "../constants/investments";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const formatCurrency = (value, currencyCode = "NGN") => {
  const amount = Number(value || 0);
  const code = (currencyCode || "NGN").toUpperCase();
  const locale = code === "NGN" ? "en-NG" : "en-US";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: code,
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
};

const normaliseTransactionType = (type = "") => type.toLowerCase();

const signedAmountFromType = (type) => {
  const normalized = normaliseTransactionType(type);
  if (normalized.includes("withdraw") || normalized.includes("transfer out")) {
    return -1;
  }
  return 1;
};

const DashboardMain = ({ adminMode = false }) => {
  const { user, refreshUser, setUser } = useContext(AuthContext);
  const isKycApproved = Boolean(user?.kycApproved ?? user?.kyc_approved ?? false);
  const kycRefreshInFlight = useRef(false);

  const [walletBalance, setWalletBalance] = useState(0);
  const [loadingWallet, setLoadingWallet] = useState(true);

  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [investments, setInvestments] = useState([]);
  const [loadingInvestments, setLoadingInvestments] = useState(false);

  const [opps, setOpps] = useState([]);
  const [loadingOpps, setLoadingOpps] = useState(false);
  const [activeInstrumentFilter, setActiveInstrumentFilter] = useState("All");

  const [modalType, setModalType] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [investModal, setInvestModal] = useState({ open: false, opportunity: null });
  const [checkingKyc, setCheckingKyc] = useState(false);
  const [opportunitySlide, setOpportunitySlide] = useState(0);

  useEffect(() => {
    if (adminMode) return;
    if (typeof refreshUser !== "function") return;
    refreshUser({ silent: true }).catch((err) => {
      console.error("Silent user refresh failed", err);
    });
  }, [adminMode, refreshUser]);

  useEffect(() => {
    if (adminMode || isKycApproved || !user?.id) {
      return;
    }
    if (typeof refreshUser !== "function") return;

    let active = true;

    const runRefresh = async () => {
      if (!active || kycRefreshInFlight.current) {
        return;
      }
      kycRefreshInFlight.current = true;
      try {
        await refreshUser({ silent: true });
      } catch (err) {
        console.error("KYC status refresh failed", err);
      } finally {
        kycRefreshInFlight.current = false;
      }
    };

    runRefresh();
    const intervalId = setInterval(runRefresh, 15000);

    return () => {
      active = false;
      clearInterval(intervalId);
      kycRefreshInFlight.current = false;
    };
  }, [adminMode, isKycApproved, refreshUser, user?.id]);

  const ensureLatestKyc = useCallback(async () => {
    if (isKycApproved) return true;
    if (typeof refreshUser !== "function") return false;

    setCheckingKyc(true);
    try {
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const refreshed = await refreshUser({ silent: attempt > 0 });
        const approved = Boolean(
          refreshed?.kycApproved ??
            refreshed?.kyc_approved ??
            user?.kycApproved ??
            user?.kyc_approved ??
            false
        );
        if (approved) {
          if (typeof setUser === "function") {
            setUser((prev) => {
              if (prev?.kycApproved) {
                return prev;
              }
              if (refreshed) {
                return { ...refreshed, kycApproved: true };
              }
              return prev ? { ...prev, kycApproved: true } : prev;
            });
          }
          return true;
        }

        if (attempt < 2) {
          const delay = attempt === 0 ? 1500 : 3000;
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
      return false;
    } catch (err) {
      console.error("KYC refresh failed", err);
      return false;
    } finally {
      setCheckingKyc(false);
    }
  }, [isKycApproved, refreshUser, setUser, user?.kycApproved, user?.kyc_approved]);

  const handleOpportunityClick = useCallback(
    async (opportunity) => {
      if (!opportunity?.is_active) return;
      const approved = await ensureLatestKyc();
      if (!approved) {
        alert("Your KYC is still pending approval. Please try again once it is marked compliant.");
        return;
      }
      setInvestModal({ open: true, opportunity });
    },
    [ensureLatestKyc]
  );

  const loadWalletSummary = useCallback(async () => {
    try {
      setLoadingWallet(true);
      const data = await api.getWalletSummary();
      const bal = data?.balance ?? data?.wallet_balance ?? 0;
      setWalletBalance(Number(bal) || 0);
      // If summary returns recent transactions we can seed the list for instant feedback
      if (Array.isArray(data?.recent_transactions) && data.recent_transactions.length) {
        setRecentTransactions((current) => {
          if (current.length) {
            return current;
          }
          return data.recent_transactions.map((txn, idx) => ({
            id: txn.id ?? idx,
            type: txn.type,
            amount: Number(txn.amount || 0),
            note: txn.note,
            timestamp: txn.timestamp,
            status: txn.status || "successful",
            statusLabel: txn.status_label,
            statusHelp: txn.status_help,
            affectsBalance: txn.affects_balance !== false,
          }));
        });
      }
    } catch (err) {
      console.error("wallet-summary failed", err);
    } finally {
      setLoadingWallet(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const rows = await api.getHistory();
      const list = (Array.isArray(rows) ? rows : []).map((r, idx) => ({
        id: r.id ?? idx,
        type: r.transaction_type || r.type || "-",
        note: r.note || r.asset || "",
        amount: Number(r.amount || 0),
        timestamp: r.timestamp || r.created_at || r.date || null,
        status: r.status || "successful",
        statusLabel: r.status_label,
        statusHelp: r.status_help,
        affectsBalance: r.affects_balance !== false,
      }));
      setRecentTransactions(list);
    } catch (err) {
      console.error("history failed", err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const loadInvestments = useCallback(async () => {
    try {
      setLoadingInvestments(true);
      const data = await api.getMyInvestments();
      setInvestments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("investments failed", err);
    } finally {
      setLoadingInvestments(false);
    }
  }, []);

  const loadOpportunities = useCallback(async () => {
    try {
      setLoadingOpps(true);
      const data = await api.getInvestmentOpportunities();
      setOpps(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("opportunities failed", err);
    } finally {
      setLoadingOpps(false);
    }
  }, []);

  useEffect(() => {
    loadWalletSummary();
    loadHistory();
    loadInvestments();
    loadOpportunities();
  }, [loadWalletSummary, loadHistory, loadInvestments, loadOpportunities, user?.id]);

  useEffect(() => {
    const tick = () => {
      loadWalletSummary();
      loadHistory();
      loadInvestments();
    };
    const id = setInterval(tick, 30000);
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        tick();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loadWalletSummary, loadHistory, loadInvestments]);

  const openModal = (type) => {
    if (adminMode) {
      return;
    }
    if (!isKycApproved && (type === "withdraw" || type === "transfer")) {
      alert("Complete your KYC documents to use this feature.");
      return;
    }
    setModalType(type);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalType(null);
  };

  const afterMoneyMovement = () => {
    loadWalletSummary();
    loadHistory();
    loadInvestments();
  };

  const groupedOpportunities = useMemo(() => {
    if (!Array.isArray(opps) || !opps.length) {
      return {};
    }
    return opps.reduce((acc, opp) => {
      const rawType = String(opp.instrument_category || opp.type || "").trim();
      const normalized = normalizeInstrumentType(rawType);
      const normalizedLower = normalized.toLowerCase();
      const rawLower = rawType.toLowerCase();
      const meta =
        instrumentMetaByValue[rawType] ||
        (rawType && rawLower === normalizedLower ? instrumentMetaByValue[normalized] : null) ||
        (!rawType ? instrumentMetaByValue[normalized] : null);
      const key = meta?.value || rawType || "Other";
      if (!acc[key]) {
        acc[key] = {
          meta: meta || (rawType ? { value: rawType, label: rawType } : { value: "Other", label: "Other" }),
          items: [],
        };
      }
      acc[key].items.push(opp);
      return acc;
    }, {});
  }, [opps]);

  const instrumentFilterOptions = useMemo(() => {
    const options = ["All"];
    instrumentOptions.forEach(({ value }) => {
      if (groupedOpportunities[value]?.items?.length) {
        options.push(value);
      }
    });
    Object.keys(groupedOpportunities)
      .filter((key) => key !== "All" && !options.includes(key))
      .sort()
      .forEach((key) => options.push(key));
    return options;
  }, [groupedOpportunities]);

  useEffect(() => {
    if (!instrumentFilterOptions.length) {
      if (activeInstrumentFilter !== "All") {
        setActiveInstrumentFilter("All");
      }
      return;
    }
    if (!instrumentFilterOptions.includes(activeInstrumentFilter)) {
      setActiveInstrumentFilter(instrumentFilterOptions[0]);
    }
  }, [instrumentFilterOptions, activeInstrumentFilter]);

  const displayedOpportunities = useMemo(() => {
    if (activeInstrumentFilter === "All") {
      return Array.isArray(opps) ? opps : [];
    }
    return groupedOpportunities[activeInstrumentFilter]?.items || [];
  }, [activeInstrumentFilter, groupedOpportunities, opps]);

  const activeInstrumentMeta = useMemo(
    () => instrumentMetaByValue[activeInstrumentFilter] || null,
    [activeInstrumentFilter]
  );

  useEffect(() => {
    if (opportunitySlide >= displayedOpportunities.length && displayedOpportunities.length) {
      setOpportunitySlide(0);
    }
  }, [displayedOpportunities.length, opportunitySlide]);

  useEffect(() => {
    setOpportunitySlide(0);
  }, [activeInstrumentFilter, displayedOpportunities.length]);

  useEffect(() => {
    if (displayedOpportunities.length <= 1) {
      return;
    }
    const id = setInterval(() => {
      setOpportunitySlide((prev) => {
        const next = prev + 1;
        return next >= displayedOpportunities.length ? 0 : next;
      });
    }, 6000);
    return () => clearInterval(id);
  }, [displayedOpportunities.length]);

  const goToOpportunitySlide = useCallback(
    (direction) => {
      if (!displayedOpportunities.length) return;
      setOpportunitySlide((prev) => {
        if (direction === "prev") {
          return prev === 0 ? displayedOpportunities.length - 1 : prev - 1;
        }
        const next = prev + 1;
        return next >= displayedOpportunities.length ? 0 : next;
      });
    },
    [displayedOpportunities.length]
  );

  const portfolioMetrics = useMemo(() => {
    if (!investments.length) {
      return {
        totalInvested: 0,
        currentValue: 0,
        averageReturn: 0,
        bestInvestment: null,
        allocation: {},
        growthPoints: [],
      };
    }

    let totalInvested = 0;
    let currentValue = 0;
    let currentPrincipal = 0;
    let bestInvestment = null;
    const allocation = {};

    investments.forEach((inv) => {
      const contributions = Number(inv.total_contributions ?? inv.amount ?? 0);
      const principal = Number(inv.principal ?? inv.amount ?? 0);
      const value = Number(inv.current_value ?? inv.value ?? inv.amount ?? 0);
      const gain = value - principal;
      const gainPct = principal > 0 ? (gain / principal) * 100 : 0;

      totalInvested += contributions;
      currentPrincipal += principal;
      currentValue += value;

      if (!bestInvestment || gainPct > (bestInvestment.gainPct ?? -Infinity)) {
        bestInvestment = { ...inv, gainPct };
      }

      const key = inv.instrument_category || inv.type || "Other";
      allocation[key] = (allocation[key] || 0) + principal;
    });

    const averageReturn =
      currentPrincipal > 0 ? ((currentValue - currentPrincipal) / currentPrincipal) * 100 : 0;

    const sorted = investments
      .filter((inv) => inv.purchase_date)
      .sort((a, b) => new Date(a.purchase_date) - new Date(b.purchase_date));

    const growthPoints = [];
    let cumulative = 0;
    sorted.forEach((inv) => {
      const contributionsForChart = Number(inv.total_contributions ?? inv.amount ?? 0);
      cumulative += contributionsForChart;
      const label = new Date(inv.purchase_date).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
      growthPoints.push({ label, value: Number(cumulative.toFixed(2)) });
    });

    return {
      totalInvested,
      currentValue,
      averageReturn,
      bestInvestment,
      allocation,
      growthPoints,
    };
  }, [investments]);

  const lineChartData = useMemo(() => {
    const points = portfolioMetrics.growthPoints;
    if (!points.length) {
      return {
        labels: ["Week 1", "Week 2", "Week 3", "Week 4"],
        datasets: [
          {
            label: "Cumulative investments",
            data: [0, 0, 0, 0],
            fill: false,
            backgroundColor: "#4A5D32",
            borderColor: "#4A5D32",
            tension: 0.3,
          },
        ],
      };
    }

    return {
      labels: points.map((point) => point.label),
      datasets: [
        {
          label: "Cumulative investments",
          data: points.map((point) => point.value),
          fill: false,
          backgroundColor: "#4A5D32",
          borderColor: "#4A5D32",
          tension: 0.3,
        },
      ],
    };
  }, [portfolioMetrics.growthPoints]);

  const doughnutData = useMemo(() => {
    const allocation = portfolioMetrics.allocation;
    const labels = Object.keys(allocation);
    const values = Object.values(allocation);

    if (!labels.length) {
      return {
        labels: ["No investments yet"],
        datasets: [
          {
            label: "Asset allocation",
            data: [1],
            backgroundColor: ["#E5E7EB"],
            borderColor: ["#fff"],
            borderWidth: 1,
          },
        ],
      };
    }

    const colours = ["#4A5D32", "#7D8F69", "#BFD1A8", "#D9E5C9", "#A5B68D", "#6F7B4E"];

    return {
      labels,
      datasets: [
        {
          label: "Asset allocation",
          data: values,
          backgroundColor: labels.map((_, index) => colours[index % colours.length]),
          borderColor: labels.map(() => "#fff"),
          borderWidth: 1,
        },
      ],
    };
  }, [portfolioMetrics.allocation]);

  const greetingName = useMemo(() => {
    if (user?.firstName || user?.lastName) {
      return [user?.firstName, user?.lastName].filter(Boolean).join(" ");
    }
    return user?.username || "there";
  }, [user?.firstName, user?.lastName, user?.username]);

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Welcome back, {greetingName}</h1>
          <p className="text-sm text-gray-500 mt-1">
            Track your balances, recent activity, and explore investment opportunities in one place.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 shadow">
            <p className="text-sm text-gray-500">Total portfolio value</p>
            <p className="text-2xl font-bold text-gray-800">{formatCurrency(portfolioMetrics.currentValue)}</p>
            <p className="text-xs text-gray-500 mt-2">based on your recorded investments</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow">
            <p className="text-sm text-gray-500">Total invested capital</p>
            <p className="text-2xl font-bold text-gray-800">{formatCurrency(portfolioMetrics.totalInvested)}</p>
            <p className="text-xs text-gray-500 mt-2">lifetime principal investment</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow">
            <p className="text-sm text-gray-500">Return earned to date</p>
            <p className="text-2xl font-bold text-gray-800">
              {portfolioMetrics.averageReturn ? `${portfolioMetrics.averageReturn.toFixed(1)}%` : "0%"}
            </p>
            <p className="text-xs text-gray-500 mt-2">accrued gain relative to current invested principal</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow">
            <p className="text-sm text-gray-500">Wallet balance</p>
            <p className="text-2xl font-bold text-gray-800">
              {loadingWallet ? "..." : formatCurrency(walletBalance)}
            </p>
            {!adminMode && (
              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => openModal("deposit")}
                  className="text-xs px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  Deposit
                </button>
                <button
                  type="button"
                  onClick={() => openModal("withdraw")}
                  className="text-xs px-3 py-1.5 rounded border border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                >
                  Withdraw
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4 shadow">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Portfolio growth</h2>
              {loadingInvestments && <span className="text-xs text-gray-500">Loading...</span>}
            </div>
            {portfolioMetrics.growthPoints.length ? (
              <Line
                data={lineChartData}
                options={{
                  responsive: true,
                  plugins: { legend: { display: false } },
                  scales: { y: { ticks: { callback: (value) => formatCurrency(value).replace("NGN", "") } } },
                }}
              />
            ) : (
              <p className="text-sm text-gray-500">No investment activity yet. Purchase an opportunity to see your growth.</p>
            )}
          </div>
          <div className="bg-white rounded-lg p-4 shadow">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Asset allocation</h2>
              {loadingInvestments && <span className="text-xs text-gray-500">Loading...</span>}
            </div>
            <Doughnut
              data={doughnutData}
              options={{
                plugins: {
                  legend: { position: "bottom" },
                },
              }}
            />
          </div>
        </div>

        {!adminMode && (
          <div className="bg-white rounded-lg p-4 shadow">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Quick wallet actions</h2>
              <button
                type="button"
                onClick={() => setIsHistoryOpen(true)}
                className="text-sm text-emerald-600 hover:underline"
              >
                View history
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { label: "Deposit", description: "Top up your wallet balance", action: () => openModal("deposit") },
                { label: "Withdraw", description: "Send funds back to your bank", action: () => openModal("withdraw") },
                { label: "Transfer", description: "Move funds to another user", action: () => openModal("transfer") },
              ].map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={item.action}
                  className="rounded-lg border border-gray-200 p-4 text-left hover:border-emerald-500 hover:shadow"
                >
                  <p className="font-medium text-gray-800">{item.label}</p>
                  <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg p-4 shadow">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">Active investments</h2>
            {loadingInvestments ? (
              <span className="text-xs text-gray-500">Refreshing...</span>
            ) : (
              <Link to="/portfolio" className="text-sm font-semibold text-emerald-600 hover:underline">
                Go to portfolio
              </Link>
            )}
          </div>
          <p className="text-sm text-gray-500">
            Detailed monitoring now lives in your Portfolio. Visit the portfolio page to see live valuations, accrued
            interest, and manage top-ups.
          </p>
          <div className="mt-3">
            <Link
              to="/portfolio"
              className="inline-flex items-center rounded border border-emerald-600 px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50"
            >
              View portfolio
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Investment opportunities</h2>
            {loadingOpps && <span className="text-xs text-gray-500">Loading...</span>}
          </div>

          {instrumentFilterOptions.length > 1 && (
            <div className="mb-4 flex flex-wrap gap-2">
              {instrumentFilterOptions.map((option) => {
                const meta = instrumentMetaByValue[option];
                const label = option === "All" ? "All products" : meta?.label || option;
                const isActive = option === activeInstrumentFilter;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setActiveInstrumentFilter(option)}
                    className={[
                      "rounded-full border px-3 py-1 text-xs font-semibold transition",
                      isActive
                        ? "border-armyGreen bg-armyGreen text-white shadow-sm"
                        : "border-gray-300 text-gray-600 hover:border-armyGreen/60 hover:text-armyGreen",
                    ].join(" ")}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {activeInstrumentFilter !== "All" && activeInstrumentMeta?.description ? (
            <p className="mb-4 text-xs text-gray-500">{activeInstrumentMeta.description}</p>
          ) : null}

          {!displayedOpportunities.length ? (
            <p className="text-sm text-gray-500">
              {activeInstrumentFilter === "All"
                ? "No opportunities available right now. Please check back soon."
                : `No ${
                    instrumentMetaByValue[activeInstrumentFilter]?.label || activeInstrumentFilter
                  } offerings are open at the moment.`}
            </p>
          ) : (
            <div className="relative">
              <div className="overflow-hidden rounded-lg">
                <div
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${opportunitySlide * 100}%)` }}
                >
                  {displayedOpportunities.map((o) => {
                    const rawType = (o.instrument_category || o.type || "").trim();
                    const normalized = normalizeInstrumentType(rawType);
                    const meta = instrumentMetaByValue[rawType] || instrumentMetaByValue[normalized] || null;
                    const instrumentLabel = meta?.label || rawType || "Other";
                    const instrumentDescription = meta?.description;
                const minAmount = Number(o.min_amount || 0);
                const rate = Number(o.annual_rate_pct || 0);
                const topUpsAllowed =
                  Boolean(o.allow_additional_investment) ||
                  (Array.isArray(o.terms) &&
                    o.terms.some((term) => term.additional_investments || term.allow_topups));
                const activeTerms = Array.isArray(o.terms) ? o.terms : [];
                const desc = (o.description || "").trim();

                    return (
                      <div key={o.id} className="w-full flex-shrink-0 px-1 md:px-2">
                        <div className="rounded-lg border border-gray-200 p-4 bg-white shadow-sm">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                                {instrumentLabel}
                          </span>
                          <span
                            className={`text-[11px] font-medium ${
                              topUpsAllowed ? "text-emerald-600" : "text-gray-500"
                            }`}
                          >
                            {topUpsAllowed ? "Top-ups enabled" : "Locked principal"}
                          </span>
                        </div>
                        <h3 className="mt-2 text-lg font-semibold text-gray-800">{o.name}</h3>
                        {instrumentDescription ? (
                          <p className="mt-1 text-xs text-gray-500">{instrumentDescription}</p>
                        ) : null}
                      </div>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                          o.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {o.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <div className="text-gray-500">Base rate (p.a.)</div>
                        <div className="font-semibold text-gray-800">{rate}%</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Minimum placement</div>
                        <div className="font-semibold text-gray-800">{formatCurrency(minAmount, o.currency)}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-gray-500">Term structures</div>
                        {activeTerms.length ? (
                          <div className="mt-1 space-y-1">
                            {activeTerms.slice(0, 3).map((term) => {
                              const termRate = Number(term.rate_pct || rate);
                              const tenorLabel = term.tenor_value
                                ? `${term.tenor_value} ${term.tenor_unit}`
                                : term.tenor_unit;
                              return (
                                <div
                                  key={`${o.id}-${term.code}`}
                                  className="flex items-center justify-between text-xs text-gray-700"
                                >
                                  <span className="font-medium">{term.label}</span>
                                  <span>
                                    {termRate ? `${termRate}%` : `${rate}%`}
                                    {" \u00b7 "}
                                    {tenorLabel}
                                  </span>
                                </div>
                              );
                            })}
                            {activeTerms.length > 3 ? (
                              <div className="text-xs text-gray-400">
                                + {activeTerms.length - 3} additional term option
                                {activeTerms.length - 3 === 1 ? "" : "s"}
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <p className="mt-1 text-xs text-gray-500">Term details will be published soon.</p>
                        )}
                      </div>
                    </div>

                    {desc && (
                      <p className="mt-3 text-sm text-gray-700">
                        {desc.length > 200 ? `${desc.slice(0, 200)}…` : desc}
                      </p>
                    )}

                    <button
                      type="button"
                      onClick={() => handleOpportunityClick(o)}
                      className={[
                        "mt-4 w-full rounded py-2 px-3 text-sm font-semibold transition-colors",
                        !o.is_active
                          ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                          : isKycApproved
                          ? "bg-armyGreen text-white hover:bg-green-700"
                          : "bg-amber-100 text-amber-700 hover:bg-amber-200",
                      ].join(" ")}
                      disabled={!o.is_active || checkingKyc}
                    >
                      {checkingKyc
                        ? "Checking KYC status..."
                        : isKycApproved
                        ? "Invest"
                        : "Verify KYC to invest"}
                    </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {displayedOpportunities.length > 1 ? (
                <>
                  <button
                    type="button"
                    onClick={() => goToOpportunitySlide("prev")}
                    className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-2 shadow hover:bg-white"
                    aria-label="Previous opportunity"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={() => goToOpportunitySlide("next")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-2 shadow hover:bg-white"
                    aria-label="Next opportunity"
                  >
                    ›
                  </button>
                  <div className="mt-4 flex justify-center gap-2">
                    {displayedOpportunities.map((item, index) => (
                      <button
                        key={item.id || index}
                        type="button"
                        onClick={() => setOpportunitySlide(index)}
                        className={`h-2 w-2 rounded-full transition ${index === opportunitySlide ? "bg-armyGreen" : "bg-gray-300 hover:bg-gray-400"}`}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg p-4 shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Recent transactions</h2>
            <button type="button" onClick={() => setIsHistoryOpen(true)} className="text-sm text-emerald-600 hover:underline">
              View all
            </button>
          </div>

          {loadingHistory ? (
            <div className="text-gray-500">Loading...</div>
          ) : !recentTransactions.length ? (
            <div className="text-gray-500">No transactions yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 px-3">Type</th>
                    <th className="py-2 px-3">Amount</th>
                    <th className="py-2 px-3">Note</th>
                    <th className="py-2 px-3">Timestamp</th>
                    <th className="py-2 px-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentTransactions.slice(0, 10).map((txn) => {
                    const amount = Number(txn.amount || 0) * signedAmountFromType(txn.type);
                    const ts = txn.timestamp ? new Date(txn.timestamp).toLocaleString() : "-";
                    return (
                      <tr key={txn.id} className="border-b">
                        <td className="py-2 px-3 capitalize">{txn.type}</td>
                        <td className="py-2 px-3">{formatCurrency(amount)}</td>
                        <td className="py-2 px-3">{txn.note || "-"}</td>
                        <td className="py-2 px-3">{ts}</td>
                        <td className="py-2 px-3">
                          <TransactionStatusBadge status={txn.status} label={txn.statusLabel} />
                          {!txn.affectsBalance && (
                            <p className="mt-1 text-xs text-gray-500">
                              {txn.statusHelp || "Not posted yet"}
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {!adminMode && isModalOpen && (
        <TransactionModal type={modalType} onClose={closeModal} onSuccess={afterMoneyMovement} />
      )}
      {isHistoryOpen && <TransactionHistoryModal onClose={() => setIsHistoryOpen(false)} />}
      {!adminMode && investModal.open && (
        <InvestmentModal
          onClose={() => setInvestModal({ open: false, opportunity: null })}
          presetId={investModal.opportunity?.id}
          onSuccess={() => {
            setInvestModal({ open: false, opportunity: null });
            afterMoneyMovement();
          }}
        />
      )}
    </div>
  );
};

export default DashboardMain;
